import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Single in-flight refresh promise. Any number of concurrent 401s will
// await the same refresh call rather than each firing their own.
let refreshPromise = null;

const doRefresh = () => {
  if (refreshPromise) return refreshPromise;
  const refresh = localStorage.getItem("refresh_token");
  if (!refresh) return Promise.reject(new Error("no_refresh_token"));
  refreshPromise = axios
    .post(`${API_URL}/api/auth/refresh`, {}, {
      headers: { Authorization: `Bearer ${refresh}` },
    })
    .then(({ data }) => {
      localStorage.setItem("access_token", data.access_token);
      return data.access_token;
    })
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error.response?.status;
    const url = error.config?.url || "";
    const isAuthEndpoint = url.includes("/auth/refresh") || url.includes("/auth/login");

    // Network errors (no response): just reject, do not touch tokens.
    if (!error.response) return Promise.reject(error);

    if (status === 401 && !error.config._retry && !isAuthEndpoint) {
      error.config._retry = true;
      try {
        const newToken = await doRefresh();
        error.config.headers.Authorization = `Bearer ${newToken}`;
        // Replay the original request. Body (JSON or FormData) is
        // preserved because we pass error.config straight through.
        return api(error.config);
      } catch {
        // Refresh genuinely failed — credentials are dead.
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        if (!window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
